/**
 * LOCAL DEVELOPMENT ONLY sign-in, so the UI can be used against scripts/dev-backend.sh
 * with no AWS account and no Cognito.
 *
 * Guarded by `import.meta.env.DEV`, which Vite replaces with the literal `false` in a
 * production build, so this whole path is dead-code-eliminated from anything deployed.
 * It also needs VITE_LOCAL_AUTH=true. In AWS the equivalent trust decision is made by
 * API Gateway's JWT authorizer; this only works against the local server, which trusts
 * these headers and binds to 127.0.0.1.
 */
export const LOCAL_AUTH: boolean =
  import.meta.env.DEV && import.meta.env['VITE_LOCAL_AUTH'] === 'true';

export interface LocalIdentity {
  userId: string;
  email: string;
  label: string;
  admin: boolean;
}

export const LOCAL_IDENTITIES: LocalIdentity[] = [
  { userId: 'local-driver', email: 'driver@local.test', label: 'Dana (driver)', admin: false },
  { userId: 'local-alice', email: 'alice@local.test', label: 'Alice (passenger)', admin: false },
  { userId: 'local-bob', email: 'bob@local.test', label: 'Bob (passenger)', admin: false },
  { userId: 'local-admin', email: 'admin@local.test', label: 'Admin', admin: true },
];

const KEY = 'wedrive.localIdentity';

export function getLocalIdentity(): LocalIdentity | null {
  if (!LOCAL_AUTH) return null;
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as LocalIdentity) : null;
  } catch {
    return null;
  }
}

export function setLocalIdentity(identity: LocalIdentity | null): void {
  if (!LOCAL_AUTH) return;
  if (identity) localStorage.setItem(KEY, JSON.stringify(identity));
  else localStorage.removeItem(KEY);
}

export function localHeaders(): Record<string, string> {
  const id = getLocalIdentity();
  if (!id) return {};
  return {
    'X-Local-User-Id': id.userId,
    'X-Local-Email': id.email,
    ...(id.admin ? { 'X-Local-Groups': 'ADMIN' } : {}),
  };
}
